/**
 * StreamingRichText
 *
 * Renders a still-arriving JAI reply with the same formatting as
 * JaiRichText (real bullets, real bold, real paragraph breaks) instead of
 * flattening it into one wall of text — only the single line currently
 * being typed gets word-by-word entrance animation; everything already
 * fully arrived (before the last newline) renders exactly like JaiRichText.
 */

import { V2 } from './JaiV2'
import { stripMarkers, toBlocks, renderBlocks, RICH_TEXT_STYLE } from './richText'

const BULLET_RE = /^[-•]\s+(.*)$/

// Everything before the last '\n' is done and will never change again —
// safe to render exactly like JaiRichText. Only the text after the last
// '\n' (or the whole string, if no '\n' has arrived yet) is still growing.
function splitFrozenAndGrowing(clean) {
  const idx = clean.lastIndexOf('\n')
  if (idx === -1) return { frozenText: '', growingLine: clean }
  return { frozenText: clean.slice(0, idx), growingLine: clean.slice(idx + 1) }
}

// Splits the growing line into word tokens that are safe to animate once
// (keyed by index so React never remounts/re-animates an earlier word) plus
// a not-yet-word-bounded partial tail. **bold** spans that have already
// closed are resolved into their own bold tokens; an unclosed ** simply
// flows through as literal characters until its closing marker arrives.
function tokenizeGrowingLine(text) {
  const boundary = Math.max(text.lastIndexOf(' '), text.lastIndexOf('\t'))
  const stable = boundary >= 0 ? text.slice(0, boundary + 1) : ''
  const partial = text.slice(boundary + 1)

  const tokens = []
  const boldRe = /\*\*(.*?)\*\*/g
  let cursor = 0
  let m
  while ((m = boldRe.exec(stable))) {
    ;(stable.slice(cursor, m.index).match(/\S+/g) || []).forEach((w) => tokens.push({ text: w, bold: false }))
    ;(m[1].match(/\S+/g) || []).forEach((w) => tokens.push({ text: w, bold: true }))
    cursor = boldRe.lastIndex
  }
  ;(stable.slice(cursor).match(/\S+/g) || []).forEach((w) => tokens.push({ text: w, bold: false }))

  return { tokens, partial }
}

export default function StreamingRichText({ content, dark = false }) {
  const clean = stripMarkers(content)
  if (!clean) return null

  const color = dark ? '#FFFFFF' : V2.navy
  const dotColor = dark ? 'rgba(255,255,255,0.55)' : V2.navy

  const cursor = (
    <span
      className="inline-block align-middle animate-pulse"
      style={{ width: 2, height: 14, background: V2.blueBright, borderRadius: 1, marginLeft: 2 }}
    />
  )

  const { frozenText, growingLine } = splitFrozenAndGrowing(clean)
  const frozenBlocks = frozenText ? toBlocks(frozenText) : []
  const growingTrimmed = growingLine.trim()
  const bulletMatch = growingTrimmed.match(BULLET_RE)
  const lastFrozen = frozenBlocks[frozenBlocks.length - 1]
  const mergeIntoList = Boolean(bulletMatch) && lastFrozen?.type === 'list'

  const { tokens, partial } = tokenizeGrowingLine(bulletMatch ? bulletMatch[1] : growingTrimmed)

  const words = tokens.map((tok, i) => (
    <span className="jai-word-in" key={i}>
      {i > 0 && ' '}
      {tok.bold ? <strong style={{ fontWeight: 700, color }}>{tok.text}</strong> : tok.text}
    </span>
  ))

  const growingInner = (
    <>
      {words}
      {tokens.length > 0 && partial && ' '}
      {partial && <span className="jai-word-partial">{partial}</span>}
      {cursor}
    </>
  )

  const growingListItem = bulletMatch ? (
    <li style={RICH_TEXT_STYLE.listItem} key="growing">
      <span style={{ ...RICH_TEXT_STYLE.dot, background: dotColor }} />
      {growingInner}
    </li>
  ) : null

  // A bare <li> outside a <ul> loses the list's `list-style: none` and the
  // browser draws its own marker on top of ours — always wrap a standalone
  // growing bullet in its own <ul> (only skipped when merging into the
  // already-frozen list below).
  const growingNode = !growingTrimmed ? null : bulletMatch ? (
    <ul style={RICH_TEXT_STYLE.list} key="growing-list">{growingListItem}</ul>
  ) : (
    <p style={{ margin: 0 }} key="growing">{growingInner}</p>
  )

  return (
    <div style={{ fontSize: 15, lineHeight: 1.65, fontWeight: 400, color }}>
      {renderBlocks(frozenBlocks, {
        color,
        dotColor,
        trimLastMargin: false,
        extraListItem: mergeIntoList ? growingListItem : null,
      })}
      {!mergeIntoList && growingNode}
      {!growingTrimmed && frozenBlocks.length > 0 && <p style={{ margin: 0 }}>{cursor}</p>}
    </div>
  )
}
